// QrScanFullscreen — modal-style camera viewer that decodes QR codes
// via jsqr on each animation frame and fires `onDecode` with the raw
// payload. Caller decides what to do with the payload (validate +
// fill a form field + close).
//
// Why a dedicated component instead of inlining:
//   - Camera permission UX is the same across pages (binding-pair page,
//     potential future scanner-driven flows).
//   - Stopping the MediaStream cleanly on unmount avoids leaking the
//     phone's torch / camera indicator after the user navigates away.
//   - The decoder loop has a few non-obvious bits (canvas re-creation,
//     willReadFrequently, requestAnimationFrame teardown) that we want
//     to write once.
//
// Camera prefs: rear-facing (`facingMode: { ideal: "environment" }`).
// Decoder: jsqr at full video resolution, ~30 Hz best-effort. iOS
// Safari + Android Chrome both decode 8-char QR within ~1 frame at
// arm's-length distance.
//
// Caller-supplied `onDecode` may return `true` to indicate "accept, stop
// scanning" — useful when the caller validates the payload and wants
// the scanner to close immediately on success. Returning `false` (or
// undefined) keeps scanning (handy for multi-scan or invalid-payload
// cases).

import React, { useEffect, useRef, useState } from "react";
import { X, Camera as CameraIcon, AlertCircle } from "lucide-react";
import jsQR from "jsqr";

export interface QrScanFullscreenProps {
  /** Body text shown above the camera viewport. Keep short. */
  title?: string;
  /** Subtitle / hint under the title. */
  hint?: string;
  /** Called with the decoded payload. Return true to close the scanner
   *  (success path); return false / undefined to keep scanning. */
  onDecode: (payload: string) => boolean | void | Promise<boolean | void>;
  /** Fired when the user dismisses without a successful decode. */
  onClose: () => void;
}

export default function QrScanFullscreen({
  title = "掃描 QR Code",
  hint,
  onDecode,
  onClose,
}: QrScanFullscreenProps): React.ReactElement {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const stoppedRef = useRef(false);
  const [err, setErr] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const v = videoRef.current;
        if (!v) return;
        v.srcObject = stream;
        await v.play().catch(() => { /* user gesture already granted at button click */ });
        setReady(true);
        scheduleDecode();
      } catch (e) {
        if (cancelled) return;
        const msg = (e as Error).name === "NotAllowedError"
          ? "需要相機權限才能掃描，請於瀏覽器設定允許。"
          : `無法開啟相機：${(e as Error).message || "未知錯誤"}`;
        setErr(msg);
      }
    })();
    return () => {
      cancelled = true;
      stoppedRef.current = true;
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scheduleDecode = (): void => {
    if (stoppedRef.current) return;
    rafRef.current = requestAnimationFrame(() => { void tryDecode(); });
  };

  const tryDecode = async (): Promise<void> => {
    if (stoppedRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current ?? document.createElement("canvas");
    canvasRef.current = c;
    if (!v || v.readyState < 2 || v.videoWidth === 0) {
      scheduleDecode();
      return;
    }
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      scheduleDecode();
      return;
    }
    ctx.drawImage(v, 0, 0, c.width, c.height);
    const img = ctx.getImageData(0, 0, c.width, c.height);
    const found = jsQR(img.data, img.width, img.height, { inversionAttempts: "dontInvert" });
    if (found?.data) {
      const accept = await Promise.resolve(onDecode(found.data));
      if (accept === true) {
        stoppedRef.current = true;
        return;
      }
    }
    scheduleDecode();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[9200] bg-black flex flex-col"
    >
      <header className="flex items-start justify-between gap-3 px-4 pt-[max(env(safe-area-inset-top),1rem)] pb-3 bg-black/80">
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold text-white">{title}</h2>
          {hint && <p className="mt-1 text-xs text-white/70 leading-relaxed">{hint}</p>}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="關閉"
          className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </header>
      <div className="relative flex-1 min-h-0 overflow-hidden">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 h-full w-full object-cover"
        />
        {/* Aiming reticle — a centered square frame ~70% of min(width,
            height) to hint where to point the camera. */}
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div className="aspect-square w-[70%] max-w-[280px] rounded-2xl border-2 border-white/60 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />
        </div>
        {!ready && !err && (
          <div className="absolute inset-0 flex items-center justify-center text-white/80 text-sm">
            <CameraIcon className="h-5 w-5 mr-2 animate-pulse" /> 正在啟動相機…
          </div>
        )}
        {err && (
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <div className="rounded-xl bg-danger/15 border border-danger/40 p-4 text-sm text-danger flex items-start gap-2 max-w-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{err}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
